import { useState, useEffect, useMemo } from "react"
import { Link } from "react-router-dom"
import { KeyRound, LogOut, ChevronLeft, ChevronRight } from "lucide-react"
import { useAuth } from "../../hooks/useAuth"
import { getRoutines } from "../../api/routines"
import { getBodyMetrics, createBodyMetric } from "../../api/bodyMetrics"
import { getWorkoutLogs, createWorkoutLog, deleteWorkoutLog } from "../../api/workoutLogs"
import { getMemberships } from "../../api/memberships"
import type { Routine, BodyMetric, WorkoutLog, Membership } from "../../types"
import { toISODate } from "../../utils/routineSchedule"
import { weekOf, weekOccurrences, completionLog, type Occurrence } from "../../utils/memberWeek"
import WorkoutSession from "./WorkoutSession"

const MUSCLE_COLORS: Record<string, string> = {
  legs: "bg-green-500/20 text-green-400",
  chest: "bg-red-500/20 text-red-400",
  back: "bg-blue-500/20 text-blue-400",
  shoulders: "bg-purple-500/20 text-purple-400",
  arms: "bg-orange-500/20 text-orange-400",
  core: "bg-yellow-500/20 text-yellow-400",
}
function getMuscleColor(group: string) {
  return MUSCLE_COLORS[group?.toLowerCase()] ?? "bg-white/10 text-white/60"
}

const WEEKDAY_SHORT = ["dom", "lun", "mar", "mié", "jue", "vie", "sáb"]
const MONTH_SHORT = [
  "ene",
  "feb",
  "mar",
  "abr",
  "may",
  "jun",
  "jul",
  "ago",
  "sep",
  "oct",
  "nov",
  "dic",
]

// "mar 15 sep" a partir de YYYY-MM-DD, sin pasar por Date (evita el corrimiento
// de zona horaria al parsear una fecha sin hora).
function formatDayLabel(iso: string) {
  const [y, m, d] = iso.split("-").map(Number)
  const wd = new Date(y, m - 1, d).getDay()
  return `${WEEKDAY_SHORT[wd]} ${d} ${MONTH_SHORT[m - 1]}`
}

function formatWeekRange(startISO: string, endISO: string) {
  const [, sm, sd] = startISO.split("-").map(Number)
  const [, em, ed] = endISO.split("-").map(Number)
  return sm === em
    ? `${sd} – ${ed} ${MONTH_SHORT[sm - 1]}`
    : `${sd} ${MONTH_SHORT[sm - 1]} – ${ed} ${MONTH_SHORT[em - 1]}`
}

const STATUS_LABEL: Record<Occurrence["status"], { text: string; className: string }> = {
  completed: { text: "Completado", className: "bg-success/20 text-success" },
  today: { text: "Hoy", className: "bg-primary/20 text-primary" },
  overdue: { text: "Atrasado", className: "bg-warning/20 text-warning" },
  upcoming: { text: "Pendiente", className: "bg-white/10 text-muted" },
}

export default function MemberDashboard() {
  const { user, logout } = useAuth()
  const today = toISODate(new Date())

  const [todayRoutines, setTodayRoutines] = useState<Routine[]>([])
  // Semana visible en "Mi semana": 0 = la actual, -1 = anterior, +1 = siguiente.
  const [weekOffset, setWeekOffset] = useState(0)
  const week = useMemo(() => weekOf(new Date(), weekOffset), [weekOffset])
  const currentWeek = useMemo(() => weekOf(new Date()), [])
  const [weekRoutines, setWeekRoutines] = useState<Routine[]>([])
  // Semana cuyas rutinas están cargadas; distinta de `week` mientras se pide.
  const [loadedWeek, setLoadedWeek] = useState<string | null>(null)
  const weekLoading = loadedWeek !== week.startISO
  const [metrics, setMetrics] = useState<BodyMetric[]>([])
  const [logs, setLogs] = useState<WorkoutLog[]>([])
  const [membership, setMembership] = useState<Membership | null>(null)
  const [loading, setLoading] = useState(true)

  // Quick log state
  const [logRoutineId, setLogRoutineId] = useState<number | null>(null)
  const [logDuration, setLogDuration] = useState("")
  const [loggingSaving, setLoggingSaving] = useState(false)

  // Workout session
  const [activeSession, setActiveSession] = useState<Routine | null>(null)

  // Quick metric state
  const [showMetricForm, setShowMetricForm] = useState(false)
  const [mWeight, setMWeight] = useState("")
  const [mFat, setMFat] = useState("")
  const [mMuscle, setMMuscle] = useState("")
  const [metricSaving, setMetricSaving] = useState(false)

  useEffect(() => {
    Promise.all([
      getRoutines({ date: today }),
      getBodyMetrics(),
      getWorkoutLogs(),
      getMemberships(),
    ])
      .then(([todayRts, bm, wl, memberships]) => {
        setTodayRoutines(todayRts)
        setMetrics(bm)
        setLogs(wl)
        setMembership(memberships[0] ?? null)
      })
      .finally(() => setLoading(false))
  }, [today])

  // Las rutinas de la semana se piden por rango (el backend ya lo soporta) y
  // se vuelven a pedir al navegar. Con series por fecha (una fila por semana)
  // pedir "todas" traía decenas de tarjetas idénticas sin fecha.
  useEffect(() => {
    let cancelled = false
    getRoutines({ from: week.startISO, to: week.endISO })
      .then((rts) => {
        if (cancelled) return
        setWeekRoutines(rts)
        setLoadedWeek(week.startISO)
      })
      .catch(() => {
        if (cancelled) return
        setWeekRoutines([])
        setLoadedWeek(week.startISO)
      })
    return () => {
      cancelled = true
    }
  }, [week])

  const occurrences = useMemo(
    () => weekOccurrences(weekRoutines, logs, week, today),
    [weekRoutines, logs, week, today],
  )

  // "Completado" de hoy usa el mismo criterio por ocurrencia que "Mi semana":
  // si el entreno de hoy se hizo otro día (o el de otro día se hizo hoy), el
  // log apunta al routine_id correcto sin importar la fecha.
  function todayLog(routine: Routine) {
    return completionLog(routine, logs, currentWeek)
  }

  async function handleUndoComplete(log: WorkoutLog) {
    await deleteWorkoutLog(log.id)
    setLogs((prev) => prev.filter((l) => l.id !== log.id))
  }

  async function handleLogWorkout() {
    // Doble clic duplicaba el log (workout_logs no tiene unicidad por rutina).
    if (!logRoutineId || loggingSaving) return
    setLoggingSaving(true)
    try {
      const log = await createWorkoutLog({
        routine_id: logRoutineId,
        performed_at: today,
        duration_minutes: logDuration ? Number(logDuration) : undefined,
      })
      setLogs((prev) => [log, ...prev])
      setLogRoutineId(null)
      setLogDuration("")
    } finally {
      setLoggingSaving(false)
    }
  }

  async function handleAddMetric() {
    setMetricSaving(true)
    try {
      const m = await createBodyMetric({
        recorded_at: today,
        weight: mWeight ? Number(mWeight) : undefined,
        body_fat: mFat ? Number(mFat) : undefined,
        muscle_mass: mMuscle ? Number(mMuscle) : undefined,
      })
      setMetrics((prev) => [m, ...prev])
      setMWeight("")
      setMFat("")
      setMMuscle("")
      setShowMetricForm(false)
    } finally {
      setMetricSaving(false)
    }
  }

  // Controles de una ocurrencia: Completado + Deshacer / captura de minutos /
  // Empezar + Completar. Mismos en "Entreno de hoy" y en "Mi semana".
  function renderCompletion(routine: Routine, log: WorkoutLog | undefined) {
    if (log) {
      return (
        <div className="flex items-center gap-2">
          <span className="rounded-full bg-success/20 px-2.5 py-1 text-xs text-success">
            Completado
          </span>
          <button
            onClick={() => handleUndoComplete(log)}
            className="rounded-full border border-white/15 px-2.5 py-1 text-xs text-muted hover:text-white hover:border-white/35 transition"
          >
            Deshacer
          </button>
        </div>
      )
    }
    if (logRoutineId === routine.id) {
      return (
        <div className="flex items-center gap-2">
          <input
            type="number"
            value={logDuration}
            onChange={(e) => setLogDuration(e.target.value)}
            placeholder="min"
            className="w-16 rounded-lg border border-white/10 bg-surface px-2 py-1 text-xs text-white outline-none focus:border-primary"
          />
          <button
            onClick={handleLogWorkout}
            disabled={loggingSaving}
            className="rounded-lg bg-success/20 px-3 py-1 text-xs text-success hover:bg-success/30 transition disabled:opacity-60"
          >
            {loggingSaving ? "..." : "Listo"}
          </button>
          <button
            onClick={() => setLogRoutineId(null)}
            className="text-xs text-muted hover:text-white"
          >
            &times;
          </button>
        </div>
      )
    }
    return (
      <div className="flex items-center gap-2">
        {routine.routine_exercises.length > 0 && (
          <button
            onClick={() => setActiveSession(routine)}
            className="rounded-full bg-primary px-3 py-1.5 text-xs font-semibold text-black hover:bg-primary-hover transition"
          >
            Empezar
          </button>
        )}
        <button
          onClick={() => setLogRoutineId(routine.id)}
          className="rounded-full border border-white/20 px-3 py-1.5 text-xs text-muted hover:text-white hover:border-white/40 transition"
        >
          Completar
        </button>
      </div>
    )
  }

  const latestMetric = metrics[0]

  if (loading) {
    return (
      <div className="flex h-screen items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-primary border-t-transparent" />
      </div>
    )
  }

  if (membership?.status === "inactive") {
    return (
      <div className="flex h-screen flex-col items-center justify-center gap-6 px-6 text-center">
        <div className="w-20 h-20 rounded-full bg-danger/10 border border-danger/30 flex items-center justify-center">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="36"
            height="36"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
            className="text-danger"
          >
            <circle cx="12" cy="12" r="10" />
            <line x1="12" y1="8" x2="12" y2="12" />
            <line x1="12" y1="16" x2="12.01" y2="16" />
          </svg>
        </div>
        <div className="space-y-2 max-w-sm">
          <h2 className="text-xl font-semibold text-white">Suscripción finalizada</h2>
          <p className="text-sm text-muted leading-relaxed">
            Su suscripción ha finalizado. Si desea continuar con otro programa, póngase en contacto
            con el coach.
          </p>
        </div>
        <button
          onClick={logout}
          className="flex items-center gap-2 rounded-lg border border-white/10 px-5 py-2.5 text-sm text-muted hover:text-white transition"
        >
          <LogOut size={15} />
          Cerrar sesión
        </button>
      </div>
    )
  }

  return (
    <div className="min-h-screen">
      {activeSession && (
        <WorkoutSession
          routine={activeSession}
          today={today}
          onComplete={() => {
            getWorkoutLogs().then(setLogs)
            setActiveSession(null)
          }}
          onClose={() => setActiveSession(null)}
        />
      )}
      {/* Header */}
      <header className="border-b border-white/10 bg-surface px-4 py-3 sm:px-6 sm:py-4">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-lg sm:text-xl font-bold tracking-wider text-white">
              SCIENCE FITNESS
            </h1>
            <p className="text-xs sm:text-sm text-muted">Welcome back, {user?.name}</p>
          </div>
          <div className="flex items-center gap-3 sm:gap-4">
            <Link
              to="/change-password"
              className="text-sm text-muted hover:text-primary transition"
              title="Cambiar contraseña"
            >
              <span className="hidden sm:inline">Cambiar contraseña</span>
              <span className="sm:hidden">
                <KeyRound size={16} />
              </span>
            </Link>
            <button
              onClick={() => logout()}
              className="text-sm text-muted hover:text-white transition"
            >
              <span className="hidden sm:inline">Sign Out</span>
              <span className="sm:hidden">
                <LogOut size={16} />
              </span>
            </button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-4xl p-4 sm:p-6 space-y-6">
        {/* Entreno de hoy */}
        <section>
          <h2 className="mb-3 text-lg font-semibold text-white">
            Entreno de hoy
            <span className="ml-2 text-sm font-normal text-muted">{formatDayLabel(today)}</span>
          </h2>

          {todayRoutines.length === 0 ? (
            <div className="rounded-xl border border-white/10 bg-surface p-6 text-center text-muted">
              <p>No hay entreno programado para hoy.</p>
              <p className="mt-1 text-xs">
                Podés abrir cualquier entreno pendiente de la semana desde &ldquo;Mi semana&rdquo;.
              </p>
            </div>
          ) : (
            <div className="space-y-3">
              {todayRoutines.map((routine) => (
                <div
                  key={routine.id}
                  className="rounded-xl border border-white/10 bg-surface overflow-hidden"
                >
                  <div className="flex items-center justify-between px-4 py-3 bg-surface-light">
                    <div>
                      <p className="font-medium text-white">{routine.name}</p>
                      <p className="text-xs text-muted">
                        {routine.routine_exercises.length} ejercicios
                      </p>
                    </div>
                    {renderCompletion(routine, todayLog(routine))}
                  </div>
                  <div className="px-4 py-3 space-y-2">
                    {routine.routine_exercises.map((re) => (
                      <div key={re.id} className="flex items-center gap-3">
                        <span
                          className={`rounded-full px-1.5 py-0.5 text-xs ${getMuscleColor(re.exercise.muscle_group)}`}
                        >
                          {re.exercise.muscle_group}
                        </span>
                        <span className="text-sm text-white flex-1">{re.exercise.name}</span>
                        <span className="text-xs text-muted">
                          {re.sets}×{re.reps}
                          {re.weight ? ` · ${re.weight} ${re.weight_unit}` : ""}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </section>

        {/* Body Metrics + Workout History */}
        <div className="grid gap-6 lg:grid-cols-2">
          {/* Metrics */}
          <section className="rounded-xl border border-white/10 bg-surface">
            <div className="flex items-center justify-between px-4 py-3 border-b border-white/10">
              <h3 className="text-sm font-semibold text-white">Body Metrics</h3>
              <button
                onClick={() => setShowMetricForm((v) => !v)}
                className="text-xs text-primary hover:text-primary-hover transition"
              >
                + Record
              </button>
            </div>

            {latestMetric && (
              <div className="grid grid-cols-3 gap-3 px-4 py-3 border-b border-white/5">
                {[
                  {
                    label: "Weight",
                    value: latestMetric.weight ? `${latestMetric.weight} kg` : "—",
                  },
                  {
                    label: "Body Fat",
                    value: latestMetric.body_fat ? `${latestMetric.body_fat}%` : "—",
                  },
                  {
                    label: "Muscle",
                    value: latestMetric.muscle_mass ? `${latestMetric.muscle_mass} kg` : "—",
                  },
                ].map((s) => (
                  <div key={s.label} className="text-center">
                    <p className="text-xs text-muted">{s.label}</p>
                    <p className="text-lg font-bold text-primary">{s.value}</p>
                  </div>
                ))}
              </div>
            )}

            {showMetricForm && (
              <div className="px-4 py-3 border-b border-white/5 space-y-2">
                <div className="grid grid-cols-3 gap-2">
                  <input
                    type="number"
                    value={mWeight}
                    onChange={(e) => setMWeight(e.target.value)}
                    placeholder="Weight"
                    className="rounded-lg border border-white/10 bg-surface-light px-2 py-1.5 text-sm text-white outline-none focus:border-primary"
                  />
                  <input
                    type="number"
                    value={mFat}
                    onChange={(e) => setMFat(e.target.value)}
                    placeholder="Fat%"
                    className="rounded-lg border border-white/10 bg-surface-light px-2 py-1.5 text-sm text-white outline-none focus:border-primary"
                  />
                  <input
                    type="number"
                    value={mMuscle}
                    onChange={(e) => setMMuscle(e.target.value)}
                    placeholder="Muscle"
                    className="rounded-lg border border-white/10 bg-surface-light px-2 py-1.5 text-sm text-white outline-none focus:border-primary"
                  />
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={handleAddMetric}
                    disabled={metricSaving}
                    className="rounded-lg bg-primary px-3 py-1.5 text-xs font-semibold text-black hover:bg-primary-hover transition disabled:opacity-50"
                  >
                    {metricSaving ? "..." : "Save"}
                  </button>
                  <button
                    onClick={() => setShowMetricForm(false)}
                    className="text-xs text-muted hover:text-white"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            )}

            {!latestMetric && !showMetricForm && (
              <p className="px-4 py-4 text-xs text-muted text-center">No metrics recorded yet.</p>
            )}
          </section>

          {/* Recent workouts */}
          <section className="rounded-xl border border-white/10 bg-surface">
            <div className="px-4 py-3 border-b border-white/10">
              <h3 className="text-sm font-semibold text-white">
                Recent Workouts
                <span className="text-muted font-normal ml-1">({logs.length})</span>
              </h3>
            </div>
            {logs.length === 0 ? (
              <p className="px-4 py-4 text-xs text-muted text-center">No workouts logged yet.</p>
            ) : (
              <div className="max-h-60 overflow-y-auto divide-y divide-white/5">
                {logs.slice(0, 10).map((l) => (
                  <div key={l.id} className="px-4 py-2.5">
                    <p className="text-sm text-white">{l.routine?.name ?? "Unknown"}</p>
                    <p className="text-xs text-muted">
                      {new Date(
                        l.performed_at.includes("T")
                          ? l.performed_at
                          : l.performed_at + "T00:00:00",
                      ).toLocaleDateString("en-US", {
                        month: "short",
                        day: "numeric",
                      })}
                      {l.duration_minutes ? ` · ${l.duration_minutes} min` : ""}
                    </p>
                  </div>
                ))}
              </div>
            )}
          </section>
        </div>

        {/* Mi semana */}
        <section>
          <div className="mb-1 flex items-center justify-between gap-3">
            <h2 className="text-sm font-semibold text-white">
              Mi semana
              <span className="ml-2 text-xs font-normal text-muted">
                {formatWeekRange(week.startISO, week.endISO)}
                {weekOffset === 0 ? " · esta semana" : ""}
              </span>
            </h2>
            <div className="flex items-center gap-1">
              <button
                onClick={() => setWeekOffset((o) => o - 1)}
                className="rounded-lg border border-white/10 p-1.5 text-muted hover:text-white hover:border-white/30 transition"
                title="Semana anterior"
                aria-label="Semana anterior"
              >
                <ChevronLeft size={14} />
              </button>
              {weekOffset !== 0 && (
                <button
                  onClick={() => setWeekOffset(0)}
                  className="rounded-lg border border-white/10 px-2 py-1 text-xs text-muted hover:text-white hover:border-white/30 transition"
                >
                  Hoy
                </button>
              )}
              <button
                onClick={() => setWeekOffset((o) => o + 1)}
                className="rounded-lg border border-white/10 p-1.5 text-muted hover:text-white hover:border-white/30 transition"
                title="Semana siguiente"
                aria-label="Semana siguiente"
              >
                <ChevronRight size={14} />
              </button>
            </div>
          </div>
          <p className="mb-3 text-xs text-muted">
            Si no pudiste entrenar el día programado, abrilo cualquier otro día: se registra con la
            fecha en que lo hagas.
          </p>

          {weekLoading && occurrences.length === 0 ? (
            <div className="rounded-xl border border-white/10 bg-surface p-6 text-center text-xs text-muted">
              Cargando…
            </div>
          ) : occurrences.length === 0 ? (
            <div className="rounded-xl border border-white/10 bg-surface p-6 text-center text-muted">
              <p>Sin entrenos esta semana.</p>
            </div>
          ) : (
            <div className={`grid gap-3 sm:grid-cols-2 ${weekLoading ? "opacity-60" : ""}`}>
              {occurrences.map(({ routine: r, date, log, status }) => (
                <div
                  key={r.id}
                  className={`rounded-xl border bg-surface p-4 ${
                    status === "today" ? "border-primary/40" : "border-white/10"
                  }`}
                >
                  <div className="mb-2 flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <p className="text-xs text-muted">
                        <span className="capitalize">{formatDayLabel(date)}</span>
                        {r.start_hour != null
                          ? ` · ${String(r.start_hour).padStart(2, "0")}:00`
                          : ""}
                      </p>
                      <p className="font-medium text-white truncate">{r.name}</p>
                      <p className="text-xs text-muted">{r.routine_exercises.length} ejercicios</p>
                    </div>
                    <span
                      className={`shrink-0 rounded-full px-2 py-0.5 text-[11px] font-medium ${STATUS_LABEL[status].className}`}
                    >
                      {STATUS_LABEL[status].text}
                    </span>
                  </div>
                  <div className="mb-3 space-y-1">
                    {r.routine_exercises.map((re) => (
                      <div key={re.id} className="flex items-center gap-2 text-xs">
                        <span
                          className={`rounded-full px-1.5 py-0.5 ${getMuscleColor(re.exercise.muscle_group)}`}
                        >
                          {re.exercise.muscle_group}
                        </span>
                        <span className="text-white truncate">{re.exercise.name}</span>
                        <span className="text-muted ml-auto shrink-0">
                          {re.sets}x{re.reps}
                        </span>
                      </div>
                    ))}
                  </div>
                  <div className="flex justify-end">{renderCompletion(r, log)}</div>
                </div>
              ))}
            </div>
          )}
        </section>
      </main>
    </div>
  )
}
